import * as THREE from "three";
import { defineModel, type Part } from "../../lib";

// Modern kit garden shed on a 2x2 tile patch: a 1.8 x 1.8 m plank cabin with
// walls ~2.0 m high, a shingle-grey gable roof (two sloped panes, gable fill
// and a ridge cap, ridge along X) and a door with a small window on the +Z
// front.
const PLANK = "#c8975a";
const SHINGLE = "#4a4a4e";
const DOOR = "#7a93a8";
const GLASS = "#9fc4d4";
const STEEL = "#aab2b8";

const SIZE = 1.8;
const WALL_H = 2.0;
const RIDGE_Y = 2.7; // apex of the gable

// Gable fill: a 3-sided cylinder makes a triangular prism. thetaStart puts
// one vertex straight up after the axis is rotated onto X; the y-scale
// flattens the equilateral section to the wanted roof rise.
const GABLE_R = SIZE / Math.sqrt(3); // base width = sqrt(3) * r = 1.8
const GABLE_SY = (RIDGE_Y - WALL_H) / (1.5 * GABLE_R);

// Roof panes: from the eave (z = +-0.95, y = 1.98) up to the ridge.
const EAVE = { y: WALL_H - 0.02, z: 0.95 };
const PANE_ANGLE = Math.atan2(RIDGE_Y + 0.02 - EAVE.y, EAVE.z);
const PANE_LEN = Math.hypot(RIDGE_Y + 0.02 - EAVE.y, EAVE.z) + 0.08;
const PANE_MID: [number, number] = [(EAVE.y + RIDGE_Y + 0.02) / 2, EAVE.z / 2];

export const gardenShed = defineModel({
  id: "garden-shed",
  batchId: "modern",
  name: "Garden shed",
  tags: ["modern", "outdoor", "garden", "shed", "storage"],
  summary: "A small plank garden shed with a grey gable roof.",
  description:
    "A garden shed of the Modern Living kit on a 2x2 tile patch: a 1.8 x " +
    "1.8 m plank cabin with 2.0 m walls, a shingle-grey gable roof with the " +
    "ridge along X, and a door with a small window on the +Z front.",
  tiles: { w: 2, h: 2 },
  footprint: 1.5,
  parts(): Part[] {
    return [
      // Plank body.
      {
        geometry: new THREE.BoxGeometry(SIZE, WALL_H, SIZE),
        color: PLANK,
        position: [0, WALL_H / 2, 0],
      },
      // Gable fill: triangular prism, axis along X, apex at the ridge.
      {
        geometry: new THREE.CylinderGeometry(GABLE_R, GABLE_R, SIZE, 3, 1, false, Math.PI / 2)
          .rotateZ(Math.PI / 2)
          .scale(1, GABLE_SY, 1),
        color: PLANK,
        position: [0, WALL_H + (GABLE_R / 2) * GABLE_SY, 0],
      },
      // Two sloped shingle panes meeting at the ridge.
      {
        geometry: new THREE.BoxGeometry(2.0, 0.05, PANE_LEN),
        color: SHINGLE,
        position: [0, PANE_MID[0], -PANE_MID[1]],
        rotation: [-PANE_ANGLE, 0, 0],
      },
      {
        geometry: new THREE.BoxGeometry(2.0, 0.05, PANE_LEN),
        color: SHINGLE,
        position: [0, PANE_MID[0], PANE_MID[1]],
        rotation: [PANE_ANGLE, 0, 0],
      },
      // Ridge cap.
      {
        geometry: new THREE.BoxGeometry(2.0, 0.07, 0.16),
        color: SHINGLE,
        position: [0, RIDGE_Y + 0.04, 0],
      },
      // Door on the +Z front, with a handle.
      {
        geometry: new THREE.BoxGeometry(0.7, 1.8, 0.04),
        color: DOOR,
        position: [-0.3, 0.9, SIZE / 2 + 0.02],
      },
      {
        geometry: new THREE.BoxGeometry(0.04, 0.12, 0.03),
        color: STEEL,
        position: [-0.05, 0.95, SIZE / 2 + 0.05],
      },
      // Small window beside the door.
      {
        geometry: new THREE.BoxGeometry(0.42, 0.42, 0.04),
        color: GLASS,
        position: [0.45, 1.45, SIZE / 2 + 0.02],
      },
    ];
  },
  animations: [],
});
